import Keycloak from "keycloak-js";
// import { RedurectHome } from "./ToggleViews";
import { BroadcastChannel } from "broadcast-channel";
import { recordError } from "../utils/logService";

const configs = {
  realm: process.env.REACT_APP_KEYCLOAK_REALM,
  url: process.env.REACT_APP_KEYCLOAK_BASE_URL,
  clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
};

if (process.env.NODE_ENV !== "development") {
  configs.realm = window._CONFIGS.REACT_APP_KEYCLOAK_REALM;
  configs.url = window._CONFIGS.REACT_APP_KEYCLOAK_BASE_URL;
  configs.clientId = window._CONFIGS.REACT_APP_KEYCLOAK_CLIENT_ID;
}

const keycloak = new Keycloak(configs);

const passwordUrl =
  configs.url +
  "/realms/" +
  configs.realm +
  "/login-actions/reset-credentials?client_id=" +
  configs.clientId;
const authChannel = new BroadcastChannel("authchannel");

const KEYCLOAK_TIMEOUT_MS = 7000;

const hasLiveKeycloakSession = () => Boolean(keycloak?.authenticated && keycloak?.token);

const forceRedirect = (redirectUri) => {
  if (typeof window !== "undefined") {
    window.location.assign(redirectUri);
  }
};

const safeKeycloakLogout = (redirectUri) => {
  try {
    if (typeof keycloak?.logout === "function") {
      const result = keycloak.logout({ redirectUri });
      if (result && typeof result.catch === "function") {
        result.catch(() => forceRedirect(redirectUri));
      }
      return;
    }
  } catch (error) {
    recordError(error);
  }

  forceRedirect(redirectUri);
};

// Auth error event for cross-component communication
const AUTH_ERROR_EVENT = "app:auth-error";

export const dispatchAuthError = (error) => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT, { detail: error }));
  }
};

export const subscribeToAuthErrors = (callback) => {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(AUTH_ERROR_EVENT, callback);
  return () => window.removeEventListener(AUTH_ERROR_EVENT, callback);
};

const parseJwtPayload = (token) => {
  if (!token) {
    return null;
  }

  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) {
      return null;
    }

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = decodeURIComponent(
      atob(base64)
        .split("")
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(""),
    );

    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

const getStoredTokenPayload = () => parseJwtPayload(localStorage.getItem("token"));

export const hasValidCachedSession = () => {
  const payload = getStoredTokenPayload();
  if (!payload?.exp) {
    return false;
  }

  return payload.exp * 1000 > Date.now();
};

export const isOfflineSessionAvailable = () => {
  return (typeof navigator !== "undefined" && !navigator.onLine) && hasValidCachedSession();
};

const getOfflineRoles = () => {
  const payload = getStoredTokenPayload();
  const realmRoles = payload?.realm_access?.roles || [];
  const resourceRoles = Object.values(payload?.resource_access || {}).flatMap(
    (resource) => resource?.roles || [],
  );

  return [...new Set([...realmRoles, ...resourceRoles])];
};

const withTimeout = (promise, ms = KEYCLOAK_TIMEOUT_MS) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Keycloak request timeout")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

const canReachKeycloak = async () => {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return false;
  }

  try {
    const probeUrl = `${configs.url}/realms/${configs.realm}/.well-known/openid-configuration`;
    const response = await withTimeout(fetch(probeUrl, { method: "GET", cache: "no-store" }), 3000);
    return Boolean(response?.ok);
  } catch {
    return false;
  }
};

/**
 * Initializes Keycloak instance
 * Calls the provided callback function if successfully authenticated.
 *
 * @param onAuthenticatedCallback
 */
export const initKeycloak = async (onAuthenticatedCallback) => {
  const keyCloakInstance = {
    initialized: false,
    failedToStart: false,
  };

  await withTimeout(
    keycloak.init({
      pkceMethod: "S256",
      onLoad: "check-sso",
      checkLoginIframe: true,
    }),
  )
    .then((initialized) => {
      if (initialized) {
        storeToken();
        onAuthenticatedCallback();
        keyCloakInstance.initialized = initialized;

        // Hack to redirect to specific route
        const redirect = sessionStorage.getItem("postLoginRedirect");
        if (redirect) {
          sessionStorage.removeItem("postLoginRedirect");
          window.location.replace(redirect);
        }
      }
      return keyCloakInstance;
    })
    .catch((error) => {
      console.log("AN ERROR HAS OCCURED WHILE INIT", error);
      if (isOfflineSessionAvailable()) {
        onAuthenticatedCallback();
        keyCloakInstance.initialized = true;
      } else {
        keyCloakInstance.failedToStart = true;
      }
    });
  return keyCloakInstance;
};

export const extendSession = (minValidity = 120) => {
  if (!hasLiveKeycloakSession()) {
    return false;
  }

  keycloak
    .updateToken(minValidity)
    .then((res) => {
      storeToken();
      return res;
    })
    .catch((err) => {
      console.error("AN ERROR HAS OCCURED WHILE EXTENDING SESSION", err);
      recordError(err);
      return err;
    });
};

export const isAuthorizedRole = (roles) => {
  if (isAuthenticated() && roles) {
    if (!keycloak.authenticated && hasValidCachedSession()) {
      const offlineRoles = getOfflineRoles();
      return roles.some((role) => offlineRoles.includes(role));
    }

    return roles.some((r) => {
      const realm = keycloak.hasRealmRole(r);
      const resource = keycloak.hasResourceRole(r);

      return realm || resource;
    });
  }
  return false;
};

export const isAuthenticated = () => {
  const response = hasLiveKeycloakSession() || hasValidCachedSession();

  return response;
};

export const logout = () => {
  //if (isAuthenticated()) {

  authChannel.postMessage("User has logged out");

  localStorage.clear();

  const redirectUri = window.location.origin + "/";
  safeKeycloakLogout(redirectUri);
  //}
};

export const clearToken = (isAnon, key = "") => {
  const params = key ? `/login/${key}/${isAnon}` : "/";
  const redirectUri = `${window.location.origin}${params}`;

  if (isAuthenticated()) {
    localStorage.removeItem("token");
  }

  safeKeycloakLogout(redirectUri);
};

export const handleLogin = () => {
  login({
    redirectUri: `${window.location.origin}`
  });
};

export const login = ({redirectUri = "/"}) => {
  if (!isAuthenticated()) {
    (async () => {
      const reachable = await canReachKeycloak();
      if (!reachable) {
        return;
      }

      keycloak
        .login({ redirectUri: `${redirectUri}` })
        .then((authenticated) => {
          if (authenticated) {
            localStorage.setItem("token", keycloak.token);
          }
        })
        .catch(() => {
          // Prevent crashing route rendering on login/navigation failures.
        });
    })();
  }
};

const isOfflineTokenExpired = () => {
  const payload = getStoredTokenPayload();
  if (!payload?.exp) {
    return true;
  }
  return payload.exp * 1000 <= Date.now();
};

export const hasTokenExpired = () => {
  if (!hasLiveKeycloakSession()) {
    const expired = isOfflineTokenExpired();
    if (expired) {
      localStorage.removeItem("token");
    }
    return expired;
  }

  const expired = keycloak.isTokenExpired();

  if (expired) {
    //! redirect keycloak login is not working as yet
    // const fullURL = loginURL + window.location.href;
    clearToken();
  }
  return expired;
};

//? TOKEN IS EXTENDED WITH 5 MINUTES
export const updateToken = (successCallback) => {
  const isLogged = hasLiveKeycloakSession();

  if (isLogged) {
    keycloak
      .updateToken(120)
      .then((authenticated) => {
        storeToken();
        return successCallback;
      })
      .catch((error) => {
        recordError(error);
        logout();
      });
  }
};

/**
 * Attempt safe token refresh on reconnect.
 * Returns true if token is now valid, false otherwise.
 * Safe to call even when offline or when Keycloak is unreachable.
 * 
 * Importance:
 * - Prevents 401 errors during offline queue replay
 * - Gracefully degrades when Keycloak is unreachable
 * - Ensures valid session before attempting server operations
 */
export const attemptTokenRefresh = async () => {
  try {
    // If we don't have a live keycloak session, use cached token validity
    if (!hasLiveKeycloakSession()) {
      const isValid = hasValidCachedSession();
      console.log("[Auth] Using cached session, valid:", isValid);
      return isValid;
    }

    // Try to refresh with Keycloak
    const canReach = await canReachKeycloak();
    if (!canReach) {
      console.warn("[Auth] Keycloak unreachable, using cached token");
      return hasValidCachedSession();
    }

    // Keycloak is reachable, update token
    const refreshed = await withTimeout(keycloak.updateToken(120));
    if (refreshed !== false) {
      storeToken();
      console.log("[Auth] Token refreshed successfully");
      return true;
    }

    return hasValidCachedSession();
  } catch (error) {
    console.error("[Auth] Token refresh failed:", error.message);
    dispatchAuthError({ type: "TOKEN_REFRESH_FAILED", error });
    return hasValidCachedSession();
  }
};

/**
 * Check if an error is a 401 (Unauthorized).
 * Used to detect token expiry during API operations.
 */
export const is401Error = (error) => {
  return error?.response?.status === 401 || error?.statusCode === 401;
};

export const getUsername = () => {
  const offlinePayload = getStoredTokenPayload();
  const response =
    isAuthenticated() &&
    (keycloak.tokenParsed?.given_name || offlinePayload?.given_name || "");
  return response;
};

export const getProfile = () => {
  const loggedIn = isAuthenticated();
  const offlinePayload = getStoredTokenPayload();

  return {
    emailAddress: loggedIn ? (keycloak.idTokenParsed?.email || offlinePayload?.email || "") : "",
    firstName: loggedIn ? (keycloak.idTokenParsed?.given_name || offlinePayload?.given_name || "") : "",
    lastName: loggedIn ? (keycloak.idTokenParsed?.family_name || offlinePayload?.family_name || "") : "",
    userId: loggedIn ? (keycloak.idTokenParsed?.sub || offlinePayload?.sub || "") : "",
    expiryTime: loggedIn ? (keycloak.idTokenParsed?.exp || offlinePayload?.exp || "") : "",

  };
};

export const getUserId = () => {
  const profile = getProfile();
  return profile?.userId;
};

export const passwordReset = () => {
  return passwordUrl;
};

export const getLogInOutText = () => {
  return isAuthenticated() ? "Logout" : "Login";
};

export const getToken = () => {
  return localStorage.getItem("token");
};

export const storeToken = () => {
  if (keycloak.token) {
    localStorage.setItem("token", keycloak.token);
  }
};

export const getTokenPayload = () => {
  const tokenArray = getToken()?.split(".");
  if(tokenArray){
    return JSON.parse(atob(tokenArray[1]));
  }

  return null;
};

export const logoutInstanceEventListener = () => {
  authChannel.onmessage = (event) => {
    logout();
    authChannel.close();
  };
};
